import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axios from 'axios';
import Cookies from 'universal-cookie';
import {
    Card,
    Container,
    Typography,
    Box,
    Button,
    TextField,
    MenuItem,
    Stack,
    Divider,
    CircularProgress,
    Alert,
} from '@mui/material';
import { API_URL } from '../../api';
import EditIcon from '@mui/icons-material/Edit';
import AccountStatusCard from '../../components/AccountStatusCard';
import AccountDangerZone from '../../components/AccountDangerZone';
import LinkedAccountsCard from '../../components/LinkedAccountsCard';
import EditAccountDetailsDialog from '../../components/EditAccountDetailsDialog';
import AccountRollupCards from '../../components/AccountRollupCards';
import YearGroupHistoryCard from '../../components/YearGroupHistoryCard';

const cookies = new Cookies();
const authHeaders = () => ({ headers: { Authorization: cookies.get('TOKEN') } });

const Field = ({ label, value }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
        <Typography color="text.secondary">{label}</Typography>
        <Typography>{value || '—'}</Typography>
    </Box>
);

export default function StudentDetailPage() {
    const { id } = useParams();
    const [student, setStudent] = useState(null);
    const [teachers, setTeachers] = useState([]);
    const [tutorId, setTutorId] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [editOpen, setEditOpen] = useState(false);

    const load = async () => {
        const { data } = await axios.get(`${API_URL}/api/admin/accounts/${id}`, authHeaders());
        setStudent(data);
        setTutorId(data?.assignedTutorId?._id || '');
    };

    useEffect(() => {
        load();
        axios
            .get(`${API_URL}/api/admin/accounts?role=teacher&limit=200`, authHeaders())
            .then((res) => setTeachers(res.data?.items || []))
            .catch(() => setTeachers([]));
    }, [id]);

    const assign = async () => {
        setBusy(true);
        setMessage('');
        setError('');
        try {
            await axios.post(
                `${API_URL}/api/admin/accounts/${id}/assign-tutor`,
                { tutorId: tutorId || null },
                authHeaders()
            );
            setMessage('Tutor assignment updated.');
            await load();
        } catch (err) {
            setError(err?.response?.data?.message || 'Failed.');
        } finally {
            setBusy(false);
        }
    };

    if (!student) {
        return (
            <Container>
                <Stack alignItems="center" sx={{ py: 6 }}>
                    <CircularProgress />
                </Stack>
            </Container>
        );
    }

    return (
        <Container>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography variant="h4">
                    {student.firstName} {student.surname}
                </Typography>
                <Button component={RouterLink} to="/students">
                    Back
                </Button>
            </Stack>

            <AccountStatusCard
                account={student}
                onChanged={(next) => next && setStudent(next)}
            />

            <AccountDangerZone account={student} />

            <LinkedAccountsCard account={student} onChange={load} />

            <Card sx={{ p: 3, mb: 3 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="h6">Contact</Typography>
                    <Button size="small" startIcon={<EditIcon />} onClick={() => setEditOpen(true)}>
                        Edit details
                    </Button>
                </Stack>
                <Field label="Email" value={student.email} />
                <Field label="Phone" value={student.phone} />
                <Field
                    label="Address"
                    value={
                        [
                            student.homeAddress?.line1,
                            student.homeAddress?.line2,
                            student.homeAddress?.city,
                            student.homeAddress?.postcode,
                        ]
                            .filter(Boolean)
                            .join(', ')
                    }
                />
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>
                    Studies
                </Typography>
                <Field label="Level" value={student.level} />
                <Field label="Subject" value={student.subject} />
                <Field label="Year group" value={student.yearGroup} />
                <Field label="School" value={student.school} />
                <Field
                    label="Date of birth"
                    value={student.dateOfBirth
                        ? new Date(student.dateOfBirth).toLocaleDateString('en-GB', {
                            day: '2-digit', month: 'short', year: 'numeric',
                        })
                        : '—'}
                />
                <YearGroupHistoryCard
                    currentYearGroup={student.yearGroup}
                    history={student.yearGroupHistory}
                />
            </Card>

            <EditAccountDetailsDialog
                open={editOpen}
                account={student}
                onClose={() => setEditOpen(false)}
                onSaved={(next) => next && setStudent(next)}
            />

            <AccountRollupCards account={student} />

            <Card sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 0.5 }}>
                    Primary tutor (individual assignment)
                </Typography>
                <Typography variant="caption" component="div" color="text.secondary" sx={{ mb: 2 }}>
                    A single "primary tutor" for individual / one-off tuition. Independent of tutoring class
                    rosters — <strong>if this student is on a class, the class's tutor is who actually teaches
                    them</strong>. Use this field for a personal tutor outside a class (e.g. exam-prep
                    one-to-ones) so admin can quickly see who to contact.
                </Typography>
                {message && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {message}
                    </Alert>
                )}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Stack direction="row" spacing={2}>
                    <TextField
                        select
                        label="Primary tutor"
                        value={tutorId}
                        onChange={(e) => setTutorId(e.target.value)}
                        sx={{ minWidth: 280 }}
                        size="small"
                    >
                        <MenuItem value="">— None —</MenuItem>
                        {teachers.map((t) => {
                            const teaches = t.teachingSubjects?.length
                                ? ' — ' +
                                  t.teachingSubjects
                                      .map((s) => `${s.subject}${s.levels?.length ? '/' + s.levels.join(',') : ''}`)
                                      .join('; ')
                                : '';
                            return (
                                <MenuItem key={t._id} value={t._id}>
                                    {t.firstName} {t.surname}{teaches}
                                </MenuItem>
                            );
                        })}
                    </TextField>
                    <Button variant="contained" disabled={busy} onClick={assign}>
                        {busy ? 'Saving…' : 'Save'}
                    </Button>
                </Stack>
            </Card>
        </Container>
    );
}
